/**
 * Web Vitals + Error Boundary instrumentation.
 *
 * Builds on the OTel tracer from otel.ts. Separated to keep otel.ts under
 * the 300-line structural limit.
 *
 * Web Vitals: LCP, CLS, INP, TTFB reported as OTel spans.
 * Error Boundaries: async/sync wrappers that trace failures.
 * Unhandled errors: window.onerror / unhandledrejection reporting.
 */

import { trace, SpanKind, SpanStatusCode } from '@opentelemetry/api'
import { onLCP, onCLS, onINP, onTTFB } from 'web-vitals'

// ---------------------------------------------------------------------------
// Web Vitals — Core Web Vitals as OTel span events
// ---------------------------------------------------------------------------

/**
 * Start collecting Core Web Vitals. Call after OTel is initialized.
 *
 * Each vital is emitted as a span with name "web.vitals.{metric}" and
 * attributes like cwv.lcp.value=2400, cwv.lcp.rating="good".
 */
export function initWebVitals(): void {
  const tracer = trace.getTracer('grizzly-web')

  const reportVital = (name: string, value: number, rating: string, id: string): void => {
    try {
      const span = tracer.startSpan(`web.vitals.${name}`, {
        kind: SpanKind.CLIENT,
        attributes: {
          [`cwv.${name}.value`]: value,
          [`cwv.${name}.rating`]: rating,
          [`cwv.${name}.id`]: id,
          'cwv.metric': name,
          'grizzly.web.route': location.hash || '#/',
        },
      })
      span.end()
    } catch {
      // Tenet 0: ignore vital reporting errors
    }
  }

  try {
    onLCP((m) => {
      reportVital('lcp', m.value, m.rating, m.id)
    })
    onCLS((m) => {
      reportVital('cls', m.value, m.rating, m.id)
    })
    onINP((m) => {
      reportVital('inp', m.value, m.rating, m.id)
    })
    onTTFB((m) => {
      reportVital('ttfb', m.value, m.rating, m.id)
    })
  } catch {
    // Tenet 0: ignore vital init errors
  }
}

// ---------------------------------------------------------------------------
// Error Boundary Spans
// ---------------------------------------------------------------------------

/**
 * Wrap an async operation in an error boundary span. If the operation throws,
 * the span is marked as error with the exception recorded. The error is
 * re-thrown so the caller can handle it.
 *
 * @param name  Span name, e.g. 'api.fleet.dashboard', 'view.render.fleet'
 * @param fn    The async function to execute inside the span
 * @param attrs Optional attributes to add to the span
 */
export async function withErrorBoundary<T>(
  name: string,
  fn: () => Promise<T>,
  attrs?: Record<string, string | number>,
): Promise<T> {
  const tracer = trace.getTracer('grizzly-web')

  const span = tracer.startSpan(name, {
    kind: SpanKind.CLIENT,
    attributes: {
      ...attrs,
      'grizzly.web.route': location.hash || '#/',
    },
  })

  try {
    const result = await fn()
    span.setStatus({ code: SpanStatusCode.OK })
    return result
  } catch (err: unknown) {
    span.setStatus({ code: SpanStatusCode.ERROR, message: String(err) })
    if (err instanceof Error) {
      span.recordException(err)
    } else {
      span.recordException(new Error(String(err)))
    }
    throw err
  } finally {
    span.end()
  }
}

/**
 * Report an unhandled error as a span. Use for window.onerror and
 * unhandledrejection handlers.
 */
export function reportUnhandledError(error: Error | string, source?: string): void {
  try {
    const tracer = trace.getTracer('grizzly-web')
    const span = tracer.startSpan('web.error.unhandled', {
      kind: SpanKind.CLIENT,
      attributes: {
        'error.type': 'unhandled',
        'error.source': source ?? 'unknown',
        'grizzly.web.route': location.hash || '#/',
      },
    })
    const err = error instanceof Error ? error : new Error(String(error))
    span.recordException(err)
    span.setStatus({ code: SpanStatusCode.ERROR, message: err.message })
    span.end()
  } catch {
    // Tenet 0: ignore
  }
}
